import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { SpaceWrapper } from "../../components";
import { getStateLabel } from "../../utils/Data";
import { useSelector } from "react-redux";
import { useAlert, useApi } from "@/";
import { selectAllPickupAddresses } from "../../app/slices";

const DetailCard = ({ title, details }) => (
  <div className="bg-background_2 border border-secondary/5 p-4 rounded-lg shadow-sm hover:shadow transition-all duration-300">
    <h3 className="text-sm font-nunito font-bold text-secondary/90 mb-3 flex items-center gap-2">
      <div className="h-5 w-1 bg-primary rounded-full"></div>
      {title}
    </h3>
    <div className="space-y-1.5 ">
      {details.map(([label, value], index) => (
        <div
          key={index}
          className="flex items-center justify-between gap-4 text-sm py-1"
        >
          <span className="text-secondary/60">{label}</span>
          <span className="text-secondary/95 font-semibold font-sans text-end !leading-4">
            {value || "N/A"}
          </span>
        </div>
      ))}
    </div>
  </div>
);

const FetchOrderDetails = () => {
  const courier = useSelector((state) => state.app?.appData?.courier) || [];
  const navigate = useNavigate();
  const pickupAddresses = useSelector(selectAllPickupAddresses);
  const { orderData } = useLocation().state || {};
  const [order, setOrder] = useState(orderData);
  const { alert, AlertDialog } = useAlert();
  const { callApi, loading } = useApi();
  const getCourierName = useCallback(
    (courierId) => {
      const courierInfo = courier.find((c) => c.id === courierId);
      return courierInfo?.name || "N/A";
    },
    [courier]
  );

  const handleExport = async (type) => {
    const loadingIndicator = document.createElement("div");
    loadingIndicator.textContent = `Downloading ${type}...`;
    loadingIndicator.style.position = "fixed";
    loadingIndicator.style.bottom = "20px";
    loadingIndicator.style.right = "20px";
    loadingIndicator.style.backgroundColor = "#4CAF50";
    loadingIndicator.style.color = "white";
    loadingIndicator.style.padding = "10px";
    loadingIndicator.style.borderRadius = "5px";
    loadingIndicator.style.zIndex = "1000";
    document.body.appendChild(loadingIndicator);

    try {
      const response = await callApi(
        `/user/export/${type.toLowerCase()}`,
        "POST",
        { shippingIds: [order.shipmentId] },
        null,
        { Accept: "application/pdf" },
        "blob"
      );

      if (!response || response.size === 0) {
        console.error("Empty response received");
        alert(`Failed to download ${type}. Please try again.`);
        document.body.removeChild(loadingIndicator);
        return;
      }

      const blob = new Blob([response], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${type}-${order._id}.pdf`;
      document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        document.body.removeChild(loadingIndicator);
      }, 2000);
    } catch (error) {
      console.error("Export error:", error);
      alert(`Failed to download ${type}. Please try again.`);
      document.body.removeChild(loadingIndicator);
    }
  };

  // Replace with your actual order data
  useEffect(() => {
    // If no order data is present (like after refresh), redirect to tracking
    if (!order) {
      navigate("/tracking");
    }
  }, [order, navigate]);
  const pickupAddress = pickupAddresses.find(
    (address) =>
      address._id === order?.pickupAddressId ||
      address.id === order?.pickupAddressId
  );

  // If no data, return null (component will redirect)
  if (!order) return null;
  const formatDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, []);


  const orderDetails = [
    {
      title: "Order",
      details: [
        ["Order No", order?.order?.orderId],
        [
          "Date",
          order?.order?.orderDate ? formatDate(order.order?.orderDate) : "N/A",
        ],
        ["Payment Type", order?.order?.paymentMode],
        ["Order Weight", order?.order?.physicalWeight],
        [
          "Dimension",
          order?.order?.dimensions
            ? `${order?.order?.dimensions.length} × ${order?.order?.dimensions.breadth} × ${order?.order?.dimensions.height}`
            : "N/A",
        ],
      ],
    },
    {
      title: "Pickup Address",
      details: [
        ["Name", pickupAddress?.contactPerson || "N/A"],
        ["Contact", pickupAddress?.contactPhone || "N/A"],
        ["Address", pickupAddress?.fullAddress || "N/A"],
        ["City", pickupAddress?.city || "N/A"],
        ["Pincode", pickupAddress?.pincode || "N/A"],
      ],
    },
    {
      title: "Delivery Address",
      details: [
        ["Name", order?.deliveryAddress?.customerName],
        ["Contact", order?.deliveryPhones?.[0]?.phoneNumber],
        [
          "Address",
          [order?.deliveryAddress?.lines?.[0]?.line1, order?.deliveryAddress?.lines?.[0]?.line2]
            .filter(Boolean)
            .join(", "),
        ],
        ["Location", getStateLabel(order?.deliveryAddress?.state)],
      ],
    },
    {
      title: "Shipment Details",
      details: [
        ["Shipment Id", order?.shipmentId],
        [
          "Shipment Date",
          order?.shipmentDate ? formatDate(order.shipmentDate) : "N/A",
        ],
        ["Service Mode", order?.serviceMode],
        ["AWB Number", order?.awbNumber],
      ],
    },
    {
      title: "Delivery Details",
      details: [
        ["Status", order?.status],
        ["Zone", order?.zone],
        ["Courier", getCourierName(order?.courierId)],
        ["Pincode", getStateLabel(order?.deliveryAddress?.pincode)],
      ],
    },
  ];

  return (
    <SpaceWrapper>
      <div className="w-full max-w-6xl mx-auto p-3 space-y-4">
        {/* Header */}
        <div className="bg-background_2/95 border-b border-secondary/5 rounded-lg shadow-sm p-4">
          <div className="flex flex-col lg:flex-row justify-between lg:items-center gap-2">
            <div>
              <h2 className="text-headline4 font-nunito font-bold text-secondary">
                Order Details
              </h2>
              <p className="text-sm text-secondary/70">
                #{order?.order?.orderId}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-1 text-xs rounded-full bg-primary/90 text-secondary/70 font-nunito font-semibold">
                {order?.status || "Booked"}
              </span>
              <div className="flex items-center">
                <button
                  className="px-3 py-1 text-xs rounded-l-lg bg-background_2 text-secondary/80 font-nunito font-medium border-y border-l border-secondary/10 hover:bg-secondary/5 transition-all duration-300 flex items-center gap-1"
                  onClick={() => handleExport("Labels")}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-3 w-3"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a2 2 0 002 2h6a2 2 0 002-2v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a2 2 0 00-2-2H7a2 2 0 00-2 2zm8 0H7v3h6V4zm0 8H7v4h6v-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Label
                </button>
                <button
                  className="px-3 py-1 text-xs rounded-r-lg bg-background_2 text-secondary/80 font-nunito font-medium border border-secondary/10 hover:bg-secondary/5 transition-all duration-300 flex items-center gap-1 border-l-0"
                  onClick={() => {
                    handleExport("Invoice");
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-3 w-3"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Invoice
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {orderDetails.map((section, index) => (
            <DetailCard key={index} {...section} />
          ))}
        </div>

        {/* Products Table */}
        <div className="bg-background_2 border border-secondary/5 rounded-lg shadow-sm p-4">
          <h3 className="text-sm font-bold font-nunito text-secondary/90 mb-3 flex items-center gap-2">
            <div className="h-5 w-1 bg-primary rounded-full"></div>
            Product Details
          </h3>
          <div className="overflow-x-auto border border-secondary/10 rounded-xl">
            <table className="min-w-full text-sm !rounded-lg overflow-hidden">
              <thead>
                <tr className="border-b border-secondary/10 bg-secondary/10">
                  {["Product", "Qty", "SKU", "HSN", "Total"].map((header) => (
                    <th
                      key={header}
                      className="text-left py-2 px-3 text-secondary/90  font-bold font-nunito"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {order?.order?.products?.map((product, index) => (
                  <tr
                    key={index}
                    className="border-b border-secondary/5 hover:bg-secondary/5"
                  >
                    <td className="py-2 px-3 text-secondary/80">
                      {product?.productName || "--"}
                    </td>
                    <td className="py-2 px-3 text-secondary/80">
                      {product?.quantity || "--"}
                    </td>
                    <td className="py-2 px-3 text-secondary/80">
                      {product?.sku || "--"}
                    </td>
                    <td className="py-2 px-3 text-secondary/80">
                      {product?.hsn || "--"}
                    </td>
                    <td className="py-2 px-3 text-secondary/80">
                      {product?.orderValue || "--"}
                    </td>
                  </tr>
                ))}
                <tr className="font-medium bg-secondary/10">
                  <td
                    colSpan="4"
                    className="py-2 px-3 text-right text-secondary/90"
                  >
                    Total
                  </td>
                  <td className="py-2 px-3 text-secondary/90 font-bold">
                    {order?.order?.totalOrderValue} Rs.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <AlertDialog />
    </SpaceWrapper>
  );
};

export default FetchOrderDetails;
